import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { addressSchema } from "@src/appV2/lib/utils/address";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { millisecondsToMinutes } from "date-fns";
import { first } from "lodash";
import { z } from "zod";

import {
  DayOfWeek,
  DEFAULT_DIGEST_SEND_DAY,
  DEFAULT_INTERVIEW_BOOKING_DAYS,
  DEFAULT_MIN_BOOKING_WINDOW,
} from "../constants";
import { transformAvailabilityToTimeSlots } from "../utils";
import { getInterviewSettingsQueryKey } from "./interviewSettingsQueryKeys";

interface UseGetInterviewSettingsParams {
  workplaceId: string;
}

export const interviewSettingsAttributesSchema = z.object({
  scheduleInterviews: z.boolean(),
  interviewLength: z.number(),
  availableInterviewers: z.number(),
  overfillFirstSlots: z.boolean(),
  skipOnTheOpenHouseWeek: z.boolean().optional(),
  selfServeInterviewBookingType: z.nativeEnum(InterviewType).optional(),
  phoneScreenRecruiters: z
    .array(
      z.object({
        workplaceUserId: z.string(),
      })
    )
    .optional(),
  interviewBookingWindow: z
    .object({
      min: z.number(),
    })
    .optional(),
  interviewBookingDays: z.number().optional(),
  availability: z.array(
    z.object({
      dayOfWeek: z.nativeEnum(DayOfWeek),
      start: z.number(),
      end: z.number(),
    })
  ),
  interviewAddress: z
    .object({
      address: addressSchema,
      coordinates: z.object({
        latitude: z.number(),
        longitude: z.number(),
      }),
    })
    .nullish(),
  interviewAddressDescription: z.string().nullish(),
  maxInterviewsPerDay: z.number().nullish(),
  requireClipboardScreening: z.boolean().optional(),
  dailyInterviewDigestTime: z
    .number()
    .int()
    .min(0)
    .max(2359)
    .refine(
      (value) => {
        const hours = Math.floor(value / 100);
        const minutes = value % 100;
        return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
      },
      { message: "Must be valid HHMM with hours 0-23 and minutes 0-59" }
    )
    .nullish(),
  digestSendDay: z.enum(["dayOf", "dayBefore"]).optional(),
  restrictInterviewsAfterDigestIsSent: z.boolean().optional(),
});

export const getInterviewSettingsForWorkplaceResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      type: z.literal(API_TYPES.interviewSettings),
      attributes: interviewSettingsAttributesSchema,
    })
  ),
  links: z.object({
    next: z.string().optional(),
    prev: z.string().optional(),
  }),
});

export type GetInterviewSettingsForWorkplaceResponse = z.infer<
  typeof getInterviewSettingsForWorkplaceResponseSchema
>;

export function transformResponseIntoInterviewSettings(
  response: GetInterviewSettingsForWorkplaceResponse
) {
  const interviewSettings = first(response.data);
  if (!isDefined(interviewSettings)) {
    throw new Error("Interview settings not found");
  }

  const minimumBookingWindowMilliseconds = interviewSettings.attributes.interviewBookingWindow?.min;

  return {
    id: interviewSettings.id,
    scheduleInterviews: interviewSettings.attributes.scheduleInterviews,
    interviewLength: millisecondsToMinutes(interviewSettings.attributes.interviewLength),
    availableInterviewers: interviewSettings.attributes.availableInterviewers,
    overfillFirstSlots: interviewSettings.attributes.overfillFirstSlots,
    skipOnTheOpenHouseWeek: interviewSettings.attributes.skipOnTheOpenHouseWeek,
    selfServeInterviewBookingType:
      interviewSettings.attributes.selfServeInterviewBookingType ??
      InterviewType.IN_PERSON_INTERVIEW,
    phoneScreenRecruiters: interviewSettings.attributes.phoneScreenRecruiters ?? [],
    minBookingWindow: !isDefined(minimumBookingWindowMilliseconds)
      ? DEFAULT_MIN_BOOKING_WINDOW
      : minimumBookingWindowMilliseconds / (60 * 60 * 1000),
    interviewBookingDays:
      interviewSettings.attributes.interviewBookingDays ?? DEFAULT_INTERVIEW_BOOKING_DAYS,
    timeSlots: transformAvailabilityToTimeSlots(interviewSettings.attributes.availability),
    interviewAddress: interviewSettings.attributes.interviewAddress,
    interviewAddressDescription: interviewSettings.attributes.interviewAddressDescription,
    maxInterviewsPerDay: interviewSettings.attributes.maxInterviewsPerDay,
    requireClipboardScreening: interviewSettings.attributes.requireClipboardScreening ?? false,
    dailyInterviewDigestTime: hmmmToTimeString(
      interviewSettings.attributes.dailyInterviewDigestTime
    ),
    digestSendDay: interviewSettings.attributes.digestSendDay ?? DEFAULT_DIGEST_SEND_DAY,
    restrictInterviewsAfterDigestIsSent:
      interviewSettings.attributes.restrictInterviewsAfterDigestIsSent ?? false,
  };
}

export type InterviewSettings = ReturnType<typeof transformResponseIntoInterviewSettings>;

function hmmmToTimeString(hhmm: number | null | undefined): string | undefined {
  if (!isDefined(hhmm)) {
    return undefined;
  }

  const hours = Math.floor(hhmm / 100);
  const minutes = hhmm % 100;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function timeStringToHhmm(timeString: string | null | undefined): number | undefined {
  if (!isDefined(timeString)) {
    return undefined;
  }

  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 100 + minutes;
}

export function useGetInterviewSettings(
  params: UseGetInterviewSettingsParams,
  options?: UseQueryOptions<InterviewSettings>
) {
  const { workplaceId } = params;
  return useQuery({
    queryKey: getInterviewSettingsQueryKey(workplaceId),
    queryFn: async () => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/interviews/settings`,
        responseSchema: getInterviewSettingsForWorkplaceResponseSchema,
      });

      return transformResponseIntoInterviewSettings(response.data);
    },
    ...options,
    meta: {
      ...options?.meta,
      logErrorMessage: APP_V2_APP_EVENTS.GET_INTERVIEW_SETTINGS_FAILURE,
    },
  });
}
